import React, { useState } from 'react';
import styles from './Feature.module.scss';
import homeStyles from '../../../styles/Home.module.scss';
import { featureIcons, emojiToIcon } from './FeatureIcons';

const categoryColors: Record<string, string> = {
    online: '#3b82f6',
    supply: '#10b981',
    instore: '#f59e0b',
    business: '#8b5cf6',
};

interface Solution {
    id: string;
    title: string;
    description: string;
    category: string;
    icon: string;
}

const Features = () => {
    const [activeCategory, setActiveCategory] = useState('online');

    const categories = [
        { id: 'online', label: 'Digital Commerce', color: '#3b82f6' },      // Blue
        { id: 'supply', label: 'Supply Chain & Logistics', color: '#10b981' }, // Green
        { id: 'instore', label: 'In-Store Technology', color: '#f59e0b' },   // Orange
        { id: 'business', label: 'Business Intelligence', color: '#8b5cf6' } // Purple (NEW)
    ];

    const solutions: Solution[] = [
        // DIGITAL COMMERCE

        {
            id: '1',
            title: 'B2C Website',
            description: 'Build customer-focused online stores with seamless browsing, secure payments, personalized offers, and smooth checkout experiences.',
            category: 'online',
            icon: '🌐'
        },
        {
            id: '2',
            title: 'B2B Website',
            description: 'Create wholesale portals with custom pricing, bulk ordering, and distributor-friendly workflows.',
            category: 'online',
            icon: '🏭'
        },
        {
            id: '3',
            title: 'Quick Commerce Website',
            description: 'Deploy high-speed storefronts optimized for hyperlocal fulfillment and rapid delivery operations.',
            category: 'online',
            icon: '🚀'
        },
        {
            id: '4',
            title: 'Marketplace Selling',
            description: 'Manage product listings across marketplaces with centralized syncing and order visibility.',
            category: 'online',
            icon: '🛒'
        },
        {
            id: '5',
            title: 'Catalog Management – AI PIM',
            description: 'Organize product catalogs using AI-powered tools for bulk uploads, mapping, and publishing.',
            category: 'online',
            icon: '🧠'
        },
        {
            id: '23',
            title: 'Mobile Commerce',
            description: 'Deliver high-performance mobile shopping experiences with native-like apps and progressive web technologies to foster digital sales.',
            category: 'online',
            icon: '📱'
        },

        // SUPPLY CHAIN

        {
            id: '6',
            title: 'Warehouse Management',
            description: 'Control warehouse operations with real-time inventory visibility and fulfillment tracking.',
            category: 'supply',
            icon: '🏬'
        },
        {
            id: '7',
            title: 'Order Management System',
            description: 'Centralize order processing across channels with automated workflows and tracking.',
            category: 'supply',
            icon: '📦'
        },
        {
            id: '8',
            title: '3PL Integration',
            description: 'Integrate third-party logistics providers for automated shipping and inventory synchronization.',
            category: 'supply',
            icon: '🚚'
        },

        // IN-STORE TECHNOLOGY

        {
            id: '9',
            title: 'POS System',
            description: 'Enable seamless billing with synchronized inventory and customer transaction tracking.',
            category: 'instore',
            icon: '💳'
        },
        {
            id: '10',
            title: 'Store Analytics',
            description: 'Monitor store performance with dashboards covering sales trends and product movement.',
            category: 'instore',
            icon: '📊'
        },
        {
            id: '11',
            title: 'Lead Management System',
            description: 'Capture and manage in-store leads while integrating with CRM platforms.',
            category: 'instore',
            icon: '🧾'
        },
        {
            id: '12',
            title: 'Sales Target & Incentive Management',
            description: 'Set targets, track achievements, and automate staff incentive calculations.',
            category: 'instore',
            icon: '🎯'
        },
        {
            id: '13',
            title: 'Customer Segment Analyzer',
            description: 'Group customers based on behavior and purchase patterns for targeted marketing.',
            category: 'instore',
            icon: '👥'
        },

        // BUSINESS INTELLIGENCE

        {
            id: '14',
            title: 'Customer Intelligence',
            description: 'Analyze customer behavior and preferences to enable personalized engagement strategies.',
            category: 'business',
            icon: '👤'
        },
        {
            id: '15',
            title: 'Sales Intelligence',
            description: 'Track sales performance, revenue trends, and forecasting insights.',
            category: 'business',
            icon: '📈'
        },
        {
            id: '16',
            title: 'Inventory Intelligence',
            description: 'Monitor stock patterns and optimize replenishment using predictive insights.',
            category: 'business',
            icon: '📦'
        },
        {
            id: '17',
            title: 'Location Intelligence',
            description: 'Use geographic insights to evaluate store performance and expansion planning.',
            category: 'business',
            icon: '📍'
        },
        {
            id: '18',
            title: 'Lead Intelligence',
            description: 'Assess lead quality and engagement to improve conversion outcomes.',
            category: 'business',
            icon: '🎯'
        },
        {
            id: '19',
            title: 'Listing Intelligence',
            description: 'Evaluate listing visibility and marketplace performance metrics.',
            category: 'business',
            icon: '🛒'
        },
        {
            id: '20',
            title: 'Realtime Intelligence',
            description: 'Access live dashboards with real-time operational and business metrics.',
            category: 'business',
            icon: '⚡'
        },
        {
            id: '21',
            title: 'Social Media Intelligence',
            description: 'Measure campaign engagement and audience growth across social platforms.',
            category: 'business',
            icon: '📱'
        },
        {
            id: '22',
            title: 'On-page Intelligence',
            description: 'Analyze website traffic, behavior, and content performance insights.',
            category: 'business',
            icon: '🌐'
        }
    ];

    const filteredSolutions = solutions.filter(solution => solution.category === activeCategory);

    // cursor-following spotlight per card
    const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
        const card = event.currentTarget;
        const rect = card.getBoundingClientRect();
        card.style.setProperty('--mx', `${event.clientX - rect.left}px`);
        card.style.setProperty('--my', `${event.clientY - rect.top}px`);
    };

    return (
        <div className={styles['container']}>
            <div className={styles['header']} style={{ textAlign: 'center', marginBottom: '60px' }}>
                <h2 className={styles['title']} style={{ fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.03em', color: '#111827' }}>
                    One platform.<br />
                    Five ways to win at

                    <div className={styles['words-container']}>
                        <div className={styles['words']}>
                            <span className={`${styles['sp']} ${homeStyles['span-gradient']}`}>B2B</span>
                            <span className={`${styles['sp']} ${homeStyles['span-gradient']}`}>B2C</span>
                            <span className={`${styles['sp']} ${homeStyles['span-gradient']}`}>Retail</span>
                            <span className={`${styles['sp']} ${homeStyles['span-gradient']}`}>AI</span>
                            <span className={`${styles['sp']} ${homeStyles['span-gradient']}`}>Insight</span>
                            <span className={`${styles['sp']} ${homeStyles['span-gradient']}`}>B2B</span>
                        </div>
                    </div>
                    commerce</h2>
                {/* <p className={styles['subtext']} style={{ maxWidth: '800px', margin: '24px auto 0', fontSize: '18px', color: '#4B5563', lineHeight: 1.6 }}>
                    Socommerz is the composable engine behind global commerce — B2B and B2C storefronts, retail lead capture, AI copilots and business intelligence. Compose exactly the stack you need, no lock-in.
                </p> */}
            </div>

            <div className={styles['categories']}>
                {categories.map(category => (
                    <button
                        key={category.id}
                        className={`${styles['category-btn']} ${activeCategory === category.id ? styles['active'] : ''}`}
                        onClick={() => setActiveCategory(category.id)}
                        style={{
                            '--category-color': category.color
                        } as React.CSSProperties}
                    >
                        <span className={styles['category-indicator']}></span>
                        {category.label}
                    </button>
                ))}
            </div>

            <div className={styles['solutions-grid']}>
                {filteredSolutions.map((solution, index) => {
                    const iconKey = emojiToIcon[solution.icon] ?? 'box';
                    const glyph = featureIcons[iconKey];
                    return (
                        <div
                            key={solution.id}
                            className={styles['solution-card']}
                            onMouseMove={handleMouseMove}
                            style={{
                                animationDelay: `${index * 0.07}s`,
                                '--accent': categoryColors[solution.category] || '#3b82f6',
                            } as React.CSSProperties}
                        >
                            <span className={styles['card-glow']} aria-hidden="true" />

                            {/* large floating watermark icon */}
                            <span className={styles['watermark']} aria-hidden="true">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                    {glyph}
                                </svg>
                            </span>

                            <div className={styles['card-top']}>
                                <span className={styles['icon-tile']}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {glyph}
                                    </svg>
                                </span>
                                <span className={styles['arrow-icon']}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                        <path d="M6.4 18L5 16.6L14.6 7H6V5H18V17H16V8.4L6.4 18Z" fill="currentColor" />
                                    </svg>
                                </span>
                            </div>

                            <div className={styles['card-content']}>
                                <h3 className={styles['card-title']}>{solution.title}</h3>
                                <p className={styles['card-description']}>{solution.description}</p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default Features;